export type WarehouseView = 'record' | 'report' | 'dashboard';
export type WarehouseMode = 'view' | 'edit';
export type WarehousePresentation = 'modal' | 'page';

export interface WarehouseMuiReadyMessage {
	type: 'warehouse:mui-ready';
}

export interface WarehouseNavigatedMessage {
	type: 'warehouse:navigated';
	view: WarehouseView;
	recordId: string | null;
	mode?: WarehouseMode;
	tab?: number;
	as?: WarehousePresentation;
	recordTab?: number;
	key?: string;
	page?: string;
	chain?: WarehouseChainEntryMessage[];
}

export interface WarehouseChainEntryMessage {
	recordId: string | number;
	segment?: string;
	tab?: number;
}

export type WarehouseInboundMessage = WarehouseMuiReadyMessage | WarehouseNavigatedMessage;

export interface WarehouseSetModeMessage {
	type: 'warehouse:set-mode';
	mode: WarehouseMode;
}

export interface WarehouseOpenRecordMessage {
	type: 'warehouse:open-record';
	as: 'modal';
	recordId: string | null;
	recordTab?: number;
	mode?: WarehouseMode;
	chain?: WarehouseChainEntryMessage[];
}

export interface WarehouseSetTabMessage {
	type: 'warehouse:set-tab';
	tab: number;
}

export type WarehouseOutboundMessage = WarehouseSetModeMessage | WarehouseOpenRecordMessage | WarehouseSetTabMessage;

const VIEWS: readonly string[] = ['record', 'report', 'dashboard'];
const MODES: readonly string[] = ['view', 'edit'];
const PRESENTATIONS: readonly string[] = ['modal', 'page'];

function isRecord(data: unknown): data is Record<string, unknown> {
	return typeof data === 'object' && data !== null;
}

function isTabIndex(value: unknown): boolean {
	return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

export function parseWarehouseInboundMessage(data: unknown): WarehouseInboundMessage | null {
	if (!isRecord(data)) return null;

	if (data.type === 'warehouse:mui-ready') {
		return { type: 'warehouse:mui-ready' };
	}

	if (data.type !== 'warehouse:navigated') return null;

	const { view, recordId, mode, tab, as, recordTab, key, page, chain } = data;
	if (typeof view !== 'string' || !VIEWS.includes(view)) return null;
	if (mode !== undefined && (typeof mode !== 'string' || !MODES.includes(mode))) return null;
	if (tab !== undefined && !isTabIndex(tab)) return null;
	if (as !== undefined && (typeof as !== 'string' || !PRESENTATIONS.includes(as))) return null;
	if (recordTab !== undefined && !isTabIndex(recordTab)) return null;
	if (key !== undefined && key !== null && typeof key !== 'string') return null;
	if (page !== undefined && page !== null && typeof page !== 'string') return null;
	if (recordId !== null && recordId !== undefined && typeof recordId !== 'string' && typeof recordId !== 'number') {
		return null;
	}
	const parsedChain = parseChain(chain);
	if (parsedChain === null) return null;

	return {
		type: 'warehouse:navigated',
		view: view as WarehouseView,
		recordId: recordId === null || recordId === undefined ? null : String(recordId),
		...(mode !== undefined ? { mode: mode as WarehouseMode } : {}),
		...(tab !== undefined ? { tab: tab as number } : {}),
		...(as !== undefined ? { as: as as WarehousePresentation } : {}),
		...(recordTab !== undefined ? { recordTab: recordTab as number } : {}),
		...(key !== undefined && key !== null ? { key: key as string } : {}),
		...(page !== undefined && page !== null && page !== '' ? { page: page as string } : {}),
		...(parsedChain.length > 0 ? { chain: parsedChain } : {}),
	};
}

function parseChain(value: unknown): WarehouseChainEntryMessage[] | null {
	if (value === undefined || value === null) return [];
	if (!Array.isArray(value)) return null;
	const entries = value.map(parseChainEntry);
	return entries.includes(null) ? null : (entries as WarehouseChainEntryMessage[]);
}

function parseChainEntry(raw: unknown): WarehouseChainEntryMessage | null {
	if (!isRecord(raw)) return null;
	const { recordId, segment, tab } = raw;
	if (typeof recordId !== 'string' && typeof recordId !== 'number') return null;
	if (String(recordId) === '') return null;
	if (segment !== undefined && typeof segment !== 'string') return null;
	if (tab !== undefined && !isTabIndex(tab)) return null;
	return {
		recordId: String(recordId),
		...(segment !== undefined ? { segment } : {}),
		...(tab !== undefined ? { tab: tab as number } : {}),
	};
}
